export interface VerticalLineItem {
  time: number;
  label: string;
  isT0: boolean;
  color: string;
  opacity?: number;
  isDashed?: boolean;
  index: number;
  badgeWidth?: number;
}

function countTradingDaysBetween(startSec: number, endSec: number): number {
  if (endSec <= startSec) return 0;
  const totalDays = Math.floor((endSec - startSec) / 86400);
  const startDay = Math.floor(startSec / 86400);
  const startDayOfWeek = (startDay + 4) % 7; // 1970-01-01 was Thursday (index 4)

  const fullWeeks = Math.floor(totalDays / 7);
  let weekendDays = fullWeeks * 2;

  const remDays = totalDays % 7;
  for (let i = 1; i <= remDays; i++) {
    const dow = (startDayOfWeek + i) % 7;
    if (dow === 0 || dow === 6) {
      weekendDays++;
    }
  }
  return totalDays - weekendDays;
}

function getXCoordinate(timeScale: any, itemTime: number, rows: any[] = []): number | null {
  if (!timeScale) return null;

  // 1. Try native timeToCoordinate first for historical candle dates
  const nativeX = timeScale.timeToCoordinate(itemTime as any);
  if (nativeX !== null && nativeX !== undefined && !isNaN(nativeX)) {
    return nativeX;
  }

  // 2. Extrapolate future dates beyond lastRowTime using trading day barStep
  if (rows && rows.length >= 2) {
    const lastRow = rows[rows.length - 1];
    const prevRow = rows[rows.length - 2];
    if (lastRow && prevRow && itemTime > lastRow.time) {
      const xLast = timeScale.timeToCoordinate(lastRow.time as any);
      const xPrev = timeScale.timeToCoordinate(prevRow.time as any);
      if (xLast !== null && xLast !== undefined && !isNaN(xLast) && xPrev !== null && xPrev !== undefined && !isNaN(xPrev)) {
        const barStep = xLast - xPrev;
        const tLast = lastRow.time;
        const tradingDays = countTradingDaysBetween(tLast, itemTime);
        const daysToUse = tradingDays > 0 ? tradingDays : Math.round((itemTime - tLast) / 86400);
        return xLast + daysToUse * barStep;
      }
    }
  }

  return null;
}

class VerticalLineRenderer {
  private _primitive: VerticalLinePrimitive;

  constructor(primitive: VerticalLinePrimitive) {
    this._primitive = primitive;
  }

  draw(target: any) {
    this._drawImpl(target);
  }

  drawBackground() {
    // Empty: foreground rendering is handled in draw() to prevent double rendering per frame
  }

  private _drawImpl(target: any) {
    const chart = this._primitive.chart;
    const series = this._primitive.series;
    const items = this._primitive.items;
    if (!chart || !series || !items || !items.length) return;

    target.useMediaCoordinateSpace((scope: any) => {
      const ctx: CanvasRenderingContext2D = scope.context;
      const timeScale = chart.timeScale();
      const canvasWidth = ctx.canvas.width;
      const canvasHeight = ctx.canvas.height;

      ctx.save();

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;
        const x = getXCoordinate(timeScale, item.time, this._primitive.rows);
        if (x === null || x === undefined || x < -50 || x > canvasWidth + 2000) continue;

        const baseAlpha = item.opacity !== undefined ? item.opacity : 0.85;

        // 1. Draw Vertical Line
        ctx.beginPath();
        ctx.setLineDash(item.isDashed ? [6, 4] : []);
        ctx.lineWidth = item.isT0 ? 2 : 1.5;
        ctx.strokeStyle = item.color;
        ctx.globalAlpha = item.isT0 ? Math.min(1.0, baseAlpha * 1.15) : baseAlpha;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }

      ctx.restore();
    });
  }
}

class VerticalLineTimeAxisView {
  private _item: VerticalLineItem;
  private _primitive: VerticalLinePrimitive;

  constructor(item: VerticalLineItem, primitive: VerticalLinePrimitive) {
    this._item = item;
    this._primitive = primitive;
  }

  coordinate(): number {
    const chart = this._primitive.chart;
    if (!chart) return -100;
    const timeScale = chart.timeScale();
    if (!timeScale) return -100;
    const x = getXCoordinate(timeScale, this._item.time, this._primitive.rows);
    return x ?? -100;
  }

  text(): string {
    return this._item.label;
  }

  textColor(): string {
    return '#ffffff';
  }

  backColor(): string {
    if (this._item.color) {
      return this._item.isT0 ? this._item.color : `${this._item.color}cc`;
    }
    return this._item.isT0 ? '#7c3aed' : '#4c1d95';
  }

  visible(): boolean {
    const x = this.coordinate();
    return x >= -50;
  }

  tickVisible(): boolean {
    return true;
  }
}

class VerticalLinePaneView {
  private _renderer: VerticalLineRenderer;

  constructor(primitive: VerticalLinePrimitive) {
    this._renderer = new VerticalLineRenderer(primitive);
  }

  zOrder(): 'top' {
    return 'top';
  }

  renderer() {
    return this._renderer;
  }
}

export class VerticalLinePrimitive {
  public chart: any = null;
  public series: any = null;
  public items: VerticalLineItem[] = [];
  public rows: any[] = [];
  public showTimeAxis: boolean = true;
  private _paneViews: VerticalLinePaneView[];
  private _timeAxisViews: VerticalLineTimeAxisView[] = [];
  private _requestUpdate: (() => void) | null = null;

  constructor(items: VerticalLineItem[], showTimeAxis: boolean = true, rows: any[] = []) {
    this.items = items;
    this.showTimeAxis = showTimeAxis;
    this.rows = rows;
    this._paneViews = [new VerticalLinePaneView(this)];
    if (showTimeAxis) {
      this._timeAxisViews = items.map(item => new VerticalLineTimeAxisView(item, this));
    }
  }

  setData(items: VerticalLineItem[], rows: any[] = []) {
    this.items = items;
    if (rows && rows.length > 0) this.rows = rows;
    if (this.showTimeAxis) {
      this._timeAxisViews = items.map(item => new VerticalLineTimeAxisView(item, this));
    }
    if (this._requestUpdate) this._requestUpdate();
  }

  attached(param: any) {
    this.chart = param.chart;
    this.series = param.series;
    this._requestUpdate = param.requestUpdate;
    if (this._requestUpdate) this._requestUpdate();
  }

  detached() {
    this.chart = null;
    this.series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {}

  paneViews() {
    return this._paneViews;
  }

  timeAxisViews() {
    return this._timeAxisViews;
  }
}


